import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, ChevronLeft, ChevronRight, Car, Loader2 } from "lucide-react";
import api from "@/lib/api";

function startOfWeek(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7;  // lundi = 0
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function toIso(d) {
  return d.toISOString().slice(0, 10);
}
function fmtJour(d) {
  return d.toLocaleDateString("fr-CH", { weekday: "short", day: "2-digit", month: "short" });
}

export default function FlottePlanning() {
  const navigate = useNavigate();
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [data, setData] = useState({ flotte: [], reservations_par_vehicule: {} });
  const [loading, setLoading] = useState(true);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const periodEnd = addDays(weekStart, 7);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/reservations/planning", {
        params: { date_debut: toIso(weekStart), date_fin: toIso(periodEnd) },
      });
      setData(d);
    } catch (e) {
      console.error("flotte planning fetch failed:", e);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, [weekStart]); // eslint-disable-line

  // Calcule l'occupation par véhicule pour la semaine. Pour chaque jour, on regarde si le véhicule est réservé.
  const isReserved = (flotteId, day) => {
    const list = data.reservations_par_vehicule[flotteId] || [];
    const dayStart = new Date(day); dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(day); dayEnd.setHours(23, 59, 59, 999);
    return list.find((r) => {
      try {
        const s = new Date(r.date_debut);
        const e = new Date(r.date_fin);
        return s < dayEnd && e > dayStart;
      } catch { return false; }
    });
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto" data-testid="flotte-planning-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <button
            type="button"
            onClick={() => navigate("/remplacement")}
            className="text-xs text-gray-500 hover:text-[#52a675] inline-flex items-center gap-1 mb-1 transition-colors"
            data-testid="flotte-planning-retour"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Véhicules de remplacement
          </button>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 flex items-center gap-2">
            <Calendar className="w-7 h-7 text-[#52a675]" /> Planning des véhicules
          </h1>
          <p className="text-sm text-gray-500 mt-1">Occupation hebdomadaire de la flotte de remplacement</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setWeekStart(addDays(weekStart, -7))} data-testid="prev-week">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" onClick={() => setWeekStart(startOfWeek(new Date()))} data-testid="today">
            Cette semaine
          </Button>
          <Button variant="outline" size="icon" onClick={() => setWeekStart(addDays(weekStart, 7))} data-testid="next-week">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
      ) : data.flotte.length === 0 ? (
        <Card className="p-10 text-center border-dashed border-2 border-gray-200">
          <Car className="w-10 h-10 mx-auto text-gray-300 mb-2" />
          <p className="text-sm text-gray-500">Aucun véhicule dans la flotte.</p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="planning-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3 font-semibold text-xs uppercase tracking-wider text-gray-500 sticky left-0 bg-gray-50 z-10 min-w-[180px]">Véhicule</th>
                  {days.map((d) => (
                    <th key={toIso(d)} className="text-center p-2 font-semibold text-xs uppercase tracking-wider text-gray-500 min-w-[110px]">
                      {fmtJour(d)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.flotte.map((f) => (
                  <tr key={f.id} className="border-t border-gray-100" data-testid={`planning-row-${f.id}`}>
                    <td className="p-3 sticky left-0 bg-white z-10">
                      <div className="font-semibold text-gray-900 text-sm">{f.marque} {f.modele}</div>
                      <code className="text-[11px] font-mono text-gray-600">{f.plaque}</code>
                    </td>
                    {days.map((d) => {
                      const r = isReserved(f.id, d);
                      return (
                        <td key={toIso(d)} className="p-1 border-l border-gray-50">
                          {r ? (
                            <button
                              type="button"
                              onClick={() => r.rdv_id && navigate("/calendrier")}
                              className="w-full h-12 bg-[#52a675]/15 hover:bg-[#52a675]/25 border border-[#52a675]/30 rounded text-[10px] text-[#2d5f3f] font-semibold text-left p-1 truncate"
                              title={`${r.client_nom || "—"} · ${r.commentaire || ""}`}
                              data-testid={`planning-cell-${f.id}-${toIso(d)}`}
                            >
                              <div className="truncate">{r.client_nom || "—"}</div>
                              {r.statut === "convertie" && <Badge variant="outline" className="text-[8px] bg-blue-50 text-blue-700 border-blue-200 mt-0.5">Prêt actif</Badge>}
                            </button>
                          ) : (
                            <div className="w-full h-12 bg-gray-50/30 border border-dashed border-gray-100 rounded" />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
